import { translate } from '@vimeo/i18n-runtime';
import { cerise } from '@vimeo/iris/color';

import Banner from './Banner';
import Meta from '../../Meta';
import FeatureGridContent from './FeatureSection/FeatureGridContent';
import FeatureSection from './FeatureSection/FeatureSection';
import FeatureStackContent from './FeatureSection/FeatureStackContent';
import { GridContent } from './FeatureSection/index.styles';
import { Description, Gradient, PageWrapper, Title, Texture, TextureWrapper } from './index.styles';
import EmailIcon from './svg/email.svg';
import LockIcon from './svg/lock.svg';
import PlayerIcon from './svg/player.svg';
import TeamIcon from './svg/team_management.svg';
import VrIcon from './svg/VR_360.svg';

const IMAGE_DIR = 'https://f.vimeocdn.com/images_v6/';

const MainContent = () => {
  return (
    <div>
      <Meta
        title="Vimeo Developer API"
        description="Build something great. Vimeo's API supports flexible, high-quality video integration with your custom apps."
      ></Meta>
      <PageWrapper>
        <div>
          <Gradient color={cerise(40)} />
          <Banner
            buttonLabel={translate('Get started')}
            description={translate(
              "Vimeo's API supports flexible, high-quality video integration with your custom apps.",
            )}
            title={translate('Build something great')}
          />
        </div>
        <div role="main" id="main">
          <FeatureSection
            descriptions={[
              translate(
                'Enjoy a full-featured upload API: privacy controls, flexible storage, and automatic transcoding to host videos in the highest quality.',
              ),
            ]}
            linkDescription={translate('about the API')}
            name="Get started"
            title={translate('Host in the highest quality.')}
            url="/api/guides/start"
          >
            <TextureWrapper>
              <Texture aria-hidden="true" src={IMAGE_DIR + 'developer/texture_1.png'} />
              <img alt="upload video" src={IMAGE_DIR + 'developer/upload_video_mock.png'} />
            </TextureWrapper>
          </FeatureSection>

          <FeatureSection
            name="The Player"
            descriptions={[
              translate(
                'Optimize your experience with a fully customizable player (with SDKs), and third-party links for web, mobile, or TV.',
              ),
            ]}
            linkDescription={translate('about the player')}
            title={translate('Customize the playback experience.')}
            url="/player/sdk"
          >
            <TextureWrapper>
              <Texture src={IMAGE_DIR + 'developer/texture_2.png'} />
              <img alt="custom video" src={IMAGE_DIR + 'developer/custom_video_mock.png'} />
            </TextureWrapper>
          </FeatureSection>
          <FeatureSection
            name="Video tools"
            descriptions={[
              translate(
                'Manage content, track metadata, and optimize videos with the powerful features included in your Vimeo membership.',
              ),
            ]}
            title={translate('Powerful video tools.')}
            includeLearnMore={false}
            url=""
          >
            <TextureWrapper>
              <Texture src={IMAGE_DIR + 'developer/texture_3.png'} />
              <img alt="team members" src={IMAGE_DIR + 'developer/team_members.png'} />
            </TextureWrapper>
          </FeatureSection>
          <FeatureStackContent
            descriptions={[
              translate(
                'Join the most passionate video community on the planet, and integrate features that connect millions of creators around the world.',
              ),
            ]}
            title={translate('Access the community.')}
          >
            <img alt="community" width="100%" src={IMAGE_DIR + 'developer/community_mock.png'} />
          </FeatureStackContent>
          <FeatureGridContent
            bgColor="#1A2E3A"
            title={translate('Professional tools to power your ideas.')}
          >
            <GridContent>
              <PlayerIcon />
              <div>
                <Title size="1">{translate('Stunning player')}</Title>
                <Description size="3">
                  {translate('Enjoy gorgeous ad-free 4K Ultra HD in the Vimeo player.')}
                </Description>
              </div>
            </GridContent>
            <GridContent>
              <VrIcon />
              <div>
                <Title size="1">{translate('360 video support')}</Title>
                <Description size="3">
                  {translate(
                    'Upload, embed, and even sell your 360 videos, and use custom controls to orient viewers.',
                  )}
                </Description>
              </div>
            </GridContent>
            <GridContent>
              <EmailIcon />
              <div>
                <Title size="1">{translate('Seamless lead generation')}</Title>
                <Description size="3">
                  {translate(
                    'Capture email addresses, and sync them with MailChimp and other email providers.',
                  )}
                </Description>
              </div>
            </GridContent>
            <GridContent>
              <LockIcon />
              <div>
                <Title size="1">{translate('Privacy features')}</Title>
                <Description size="3">
                  {translate(
                    'Lock your videos with password protection, domain-level privacy, and private link sharing.',
                  )}
                </Description>
              </div>
            </GridContent>
            <GridContent>
              <TeamIcon />
              <div>
                <Title size="1">{translate('Team-friendly environment')}</Title>
                <Description size="3">
                  {translate('Assign multiple users to manage your videos and Vimeo account.')}
                </Description>
              </div>
            </GridContent>
          </FeatureGridContent>
          <div style={{ backgroundColor: '#3B5A6C' }}>
            <img alt="footer halo" src={IMAGE_DIR + 'developer/footer_halo.png'} width="100%" />
          </div>
        </div>
      </PageWrapper>
    </div>
  );
};

export default MainContent;
